/**
 * Curriculum API Service
 * Fetches boards, grades, subjects, and chapters from the backend
 */
import axios from 'axios';
import { apiUrl } from './config';
import { getAuthToken, getRefreshToken, getPortalType, setAuthTokens, getUserData } from '../utils/auth';

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Token refresh function
const refreshAccessToken = async (portal) => {
  try {
    const refreshToken = getRefreshToken(portal);
    if (!refreshToken) {
      return null;
    }

    const response = await axios.post(apiUrl('users/token/refresh/'), {
      refresh: refreshToken
    });

    if (response.data?.access) {
      const userData = getUserData(portal) || {};
      const newRefreshToken = response.data.refresh || refreshToken;
      setAuthTokens(portal, response.data.access, newRefreshToken, userData);
      return response.data.access;
    }
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
};

// Create axios instance with interceptors
const curriculumApi = axios.create();

// Request interceptor to add auth token
curriculumApi.interceptors.request.use(
  (config) => {
    const portal = getPortalType(window.location.pathname);
    const token = getAuthToken(portal);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('⚠️ No auth token found for portal:', portal);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling with token refresh
curriculumApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const portal = getPortalType(window.location.pathname);

    // If error is 401 and we haven't tried refreshing yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return curriculumApi(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken(portal);
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          isRefreshing = false;
          return curriculumApi(originalRequest);
        } else {
          // Refresh failed, redirect to login
          processQueue(error, null);
          isRefreshing = false;
          console.warn('⚠️ Token refresh failed, redirecting to login');
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        console.warn('⚠️ Token refresh error, redirecting to login');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // For other errors, just reject
    return Promise.reject(error);
  }
);

// Get auth token from localStorage (for backward compatibility)
const getAuthHeaders = () => {
  // Get token based on portal
  const portal = getPortalType(window.location.pathname);
  const token = getAuthToken(portal);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Subjects API
export const subjectsAPI = {
  list: async (params = {}, signal) => {
    try {
      const response = await curriculumApi.get(apiUrl('superadmin/subjects/'), {
        params,
        signal,
      });
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching subjects:', error);
      throw error;
    }
  },
};

// Chapters API
export const chaptersAPI = {
  list: async (params = {}, signal) => {
    try {
      const response = await curriculumApi.get(apiUrl('superadmin/chapters/'), {
        params,
        signal,
      });
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching chapters:', error);
      throw error;
    }
  },
  
  getProcessingStatus: async (chapterId) => {
    try {
      const response = await curriculumApi.get(apiUrl(`superadmin/chapters/${chapterId}/processing-status/`));
      return response.data;
    } catch (error) {
      console.error(`Error fetching processing status for chapter ${chapterId}:`, error);
      throw error;
    }
  },
  
  getTaskStatus: async (taskId) => {
    try {
      const response = await curriculumApi.get(apiUrl('superadmin/chapters/task-status/'), {
        params: { task_id: taskId }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching task status for task ${taskId}:`, error);
      throw error;
    }
  },
};

